import type {
  CallLogInput, ContactInput, CrmConversationItem, DuplicateCheckInput, DuplicateKind, DuplicateMatch, DuplicatePair, ImportListItem, ImportRowView,
  Lineage, MembershipInput, MergePreview, OrganisationLinks, OwnerOption, Page, PersonLinks, RefName, RelationshipInput, RollbackCheck,
} from '@takeit4me/shared'
import type { Http } from '../http'

type In<T extends { _zod: { input: unknown } }> = T['_zod']['input']
type Paging = { limit?: number; cursor?: string | null }
const enc = encodeURIComponent

export type MembershipBody = In<typeof MembershipInput>
export type RelationshipBody = In<typeof RelationshipInput>
export type DuplicateCheckBody = In<typeof DuplicateCheckInput>
export type ContactBody = In<typeof ContactInput>
export type CallLogBody = In<typeof CallLogInput>
export interface CrmConversationQuery extends Paging {
  state?: string; q?: string; channel?: string; assigned?: 'me' | 'unassigned' | string; person_id?: string; organisation_id?: string
}

/** Console API methods: workstream D (record screens, conversations, merges, duplicates, roles, lineage, imports). */
export interface PeopleApi {
  /** Staff who can be an account manager. */
  crmOwners(): Promise<OwnerOption[]>
  personLinks(id: string): Promise<PersonLinks>
  organisationLinks(id: string): Promise<OrganisationLinks>
  setPersonOwner(id: string, ownerId: string | null): Promise<PersonLinks>
  setOrganisationOwner(id: string, ownerId: string | null): Promise<OrganisationLinks>
  setPrimaryContact(organisationId: string, personId: string | null): Promise<OrganisationLinks>
  setMembership(personId: string, body: MembershipBody): Promise<PersonLinks>
  removeMembership(personId: string, organisationId: string): Promise<PersonLinks>
  addRelationship(personId: string, body: RelationshipBody): Promise<PersonLinks>
  removeRelationship(personId: string, relationshipId: string): Promise<PersonLinks>
  removeIdentifier(personId: string, identifierId: string): Promise<{ ok: true }>
  /** Where a record came from: object is person, organisation or a custom object key. */
  lineage(object: string, id: string): Promise<Lineage>
  /** Names for reference field values. */
  resolveRefs(refs: { object: string; id: string }[]): Promise<RefName[]>
  mergePreview(kind: DuplicateKind, from: string, into: string): Promise<MergePreview>
  merge(kind: DuplicateKind, from: string, into: string): Promise<{ change_id: string; into: string }>
  undoMerge(kind: DuplicateKind, id: string): Promise<{ ok: true }>
  duplicates(kind: DuplicateKind, opts?: { dismissed?: boolean }): Promise<{ items: DuplicatePair[]; ai: 'ok' | 'unavailable' | 'not_needed' }>
  decideDuplicate(body: { kind: DuplicateKind; a: string; b: string; status: 'dismissed' | 'open' }): Promise<{ ok: true }>
  checkDuplicates(body: DuplicateCheckBody): Promise<DuplicateMatch[]>
  listImports(q?: Paging): Promise<Page<ImportListItem>>
  rollbackCheck(importId: string): Promise<RollbackCheck>
  importRows(importId: string, q?: { offset?: number; limit?: number; action?: string }): Promise<{ items: ImportRowView[]; total: number }>
  crmConversations(q?: CrmConversationQuery): Promise<Page<CrmConversationItem>>
  /** Email or SMS from the record, through the send gate. */
  contactPerson(personId: string, body: ContactBody): Promise<{ conversation_id: string; message_id: string; conversation_created: boolean }>
  logCall(personId: string, body: CallLogBody): Promise<{ call_id: string; conversation_id: string }>
}

export const peopleMethods = (http: Http): PeopleApi => ({
  crmOwners: () => http.get<OwnerOption[]>('/crm/owners'),
  personLinks: (id) => http.get<PersonLinks>(`/crm/people/${enc(id)}/links`),
  organisationLinks: (id) => http.get<OrganisationLinks>(`/crm/organisations/${enc(id)}/links`),
  setPersonOwner: (id, owner_id) => http.request<PersonLinks>('PUT', `/crm/people/${enc(id)}/owner`, { owner_id }),
  setOrganisationOwner: (id, owner_id) => http.request<OrganisationLinks>('PUT', `/crm/organisations/${enc(id)}/owner`, { owner_id }),
  setPrimaryContact: (id, person_id) => http.request<OrganisationLinks>('PUT', `/crm/organisations/${enc(id)}/primary`, { person_id }),
  setMembership: (id, body) => http.request<PersonLinks>('PUT', `/crm/people/${enc(id)}/memberships`, body),
  removeMembership: (id, org) => http.delete<PersonLinks>(`/crm/people/${enc(id)}/memberships/${enc(org)}`),
  addRelationship: (id, body) => http.post<PersonLinks>(`/crm/people/${enc(id)}/relationships`, body),
  removeRelationship: (id, rel) => http.delete<PersonLinks>(`/crm/people/${enc(id)}/relationships/${enc(rel)}`),
  removeIdentifier: (id, ident) => http.delete<{ ok: true }>(`/crm/people/${enc(id)}/identifiers/${enc(ident)}`),
  lineage: (object, id) => http.get<Lineage>(`/crm/lineage/${enc(object)}/${enc(id)}`),
  resolveRefs: (refs) => http.post<RefName[]>('/crm/refs', { refs }),
  mergePreview: (kind, from, into) => http.get<MergePreview>('/crm/merges/preview', { kind, from, into }),
  merge: (kind, from, into) => http.post<{ change_id: string; into: string }>('/crm/merges', { kind, from, into }),
  undoMerge: (kind, id) => http.post<{ ok: true }>('/crm/merges/undo', { kind, id }),
  duplicates: (kind, opts = {}) => http.get<{ items: DuplicatePair[]; ai: 'ok' | 'unavailable' | 'not_needed' }>('/crm/duplicates', { kind, dismissed: opts.dismissed ? 1 : undefined }),
  decideDuplicate: (body) => http.post<{ ok: true }>('/crm/duplicates/decide', body),
  checkDuplicates: (body) => http.post<DuplicateMatch[]>('/crm/duplicates/check', body),
  listImports: (q = {}) => http.get<Page<ImportListItem>>('/crm/imports', q),
  rollbackCheck: (id) => http.get<RollbackCheck>(`/crm/imports/${enc(id)}/rollback-check`),
  importRows: (id, q = {}) => http.get<{ items: ImportRowView[]; total: number }>(`/crm/imports/${enc(id)}/rows`, q),
  crmConversations: (q = {}) => http.get<Page<CrmConversationItem>>('/crm/conversations', { ...q }),
  contactPerson: (id, body) => http.post<{ conversation_id: string; message_id: string; conversation_created: boolean }>(`/crm/people/${enc(id)}/contact`, body),
  logCall: (id, body) => http.post<{ call_id: string; conversation_id: string }>(`/crm/people/${enc(id)}/calls`, body),
})
